/* 
Utility functions for handling event details, metadata generation, and location data. 
*/

import { EventDetail } from '@/sanity/fetchEventDetail';
import { urlFor } from '@/sanity/sanityClient';
import { isSanityImage } from '@/shared/types/imageMeta';
import {
  CONTENT_TYPE_ARTICLE,
  CONTENT_TYPE_CATEGORY,
  CONTENT_TYPE_EVENT,
  CONTENT_TYPE_PAGE,
  URL_PATH_ARTICLE,
  URL_PATH_CATEGORY,
  URL_PATH_EVENT,
} from '@/shared/constants';
import { EventJsonLd } from '@/shared/types/eventData';
import { LocationData } from '@/shared/types/location';
import { Seo } from '@/shared/types/seo';
import { EventDetailData } from '@/shared/types/contentModule';
import { Metadata } from 'next';
import { formatISO, isAfter, parseISO, startOfDay } from 'date-fns';

// Determine if the preview mode is active (used on preview environment preview.heuteinhamburg.de)
export function isPreviewMode() {
  return process.env.NEXT_PUBLIC_SANITY_PREVIEW_MODE === 'true';
}

// Generate default metadata for SEO and social sharing
export function generateDefaultMetadata(data: any, path: string) {
  return {
    title: data.seo?.metaTitle || data.title,
    description: data.seo?.metaDescription || data.shortDescription || '',
    alternates: {
      canonical: path,
    },
    openGraph: {
      title: data.seo?.socialTitle || data.title,
      images: [
        {
          url: data.coverImage?.image
            ? urlFor(data.coverImage?.image, 1200, 630).url()
            : '/images/sharing.png', // Fallback image in the public folder
          width: 1200,
          height: 630,
          alt: data.coverImage?.alt || 'Heute in Hamburg', // Provide a default alt text
        },
      ],
    },
    robots: {
      index: data.seo?.hideFromSearchEngines ? false : true,
      follow: data.seo?.hideFromSearchEngines ? false : true,
    },
  };
}

// Generate metadata from REST API data (plain image URL, no Sanity image builder).
// SEO overrides come from an optional backend `seo` object; otherwise falls back to
// the page title / shortDescription. The OG image is resized via Sanity CDN params.
export function generateRestMetadata({
  title,
  description,
  imageUrl,
  imageAlt,
  seo,
  path,
}: {
  title: string;
  description?: string;
  imageUrl?: string;
  imageAlt?: string;
  seo?: Seo | null;
  path: string;
}): Metadata {
  const ogImage = imageUrl
    ? `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}w=1200&h=630&fit=crop&fm=jpg`
    : '/images/sharing.png';
  const hideFromSearch = seo?.hideFromSearchEngines ?? false;

  return {
    title: seo?.metaTitle || title,
    description: seo?.metaDescription || description || '',
    alternates: {
      canonical: path,
    },
    openGraph: {
      title: seo?.socialTitle || title,
      images: [
        {
          url: ogImage,
          width: 1200,
          height: 630,
          alt: imageAlt || title || 'Heute in Hamburg',
        },
      ],
    },
    robots: {
      index: !hideFromSearch,
      follow: !hideFromSearch,
    },
  };
}

// Generate JSON-LD schema for future events for Google
export function generateFutureEventsJsonLd(event: EventDetail): EventJsonLd[] {
  const currentDate = startOfDay(new Date());

  const futureEventDates = event.eventDates
    ? event.eventDates?.filter((eventDate) => {
        const startDate = formatISO(eventDate.startDate);
        return isAfter(startDate, currentDate);
      })
    : [];

  return futureEventDates.map((eventDate) => {
    let startDateISO;
    let endDateISO;

    if (event.startTime) {
      startDateISO = formatISO(parseISO(`${eventDate.startDate}T${event.startTime}:00`));
    }

    if (event.endTime) {
      endDateISO = formatISO(
        parseISO(
          `${eventDate.endDate ? eventDate.endDate : eventDate.startDate}T${event.endTime}:00`,
        ),
      );
    }

    const jsonLd: EventJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: event.name,
      ...(startDateISO && { startDate: startDateISO }),
      ...(endDateISO && { endDate: endDateISO }),
      location: {
        '@type': 'Place',
        name: event.location?.name,
        ...(event.location?.address && {
          address: {
            '@type': 'PostalAddress',
            streetAddress: event.location.address,
          },
        }),
        ...(event.location?.geopoint && {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: event.location?.geopoint.lat,
            longitude: event.location?.geopoint.lng,
          },
        }),
      },
      image:
        event.coverImage && isSanityImage(event.coverImage)
          ? [urlFor(event.coverImage.image, 1200, 628).url()]
          : [],
      description: event.shortDescription,
      offers: {
        '@type': 'Offer',
        ...(event.ticketLink && { offersUrl: event.ticketLink }),
        priceCurrency: 'EUR',
        ...(startDateISO && { validFrom: startDateISO }),
      },
    };

    return jsonLd;
  });
}

// JSON-LD schema for future event dates, fed from REST EventDetailData
// (plain image URL, no Sanity image builder).
export function generateRestFutureEventsJsonLd(detail: EventDetailData): EventJsonLd[] {
  const currentDate = startOfDay(new Date());

  const futureEventDates = detail.eventDates.filter((eventDate) =>
    isAfter(parseISO(eventDate.startDate), currentDate),
  );

  const imageUrl =
    detail.image && !isSanityImage(detail.image) ? detail.image.imageUrl : undefined;

  return futureEventDates.map((eventDate) => {
    let startDateISO: string | undefined;
    let endDateISO: string | undefined;

    if (detail.startTime) {
      startDateISO = formatISO(parseISO(`${eventDate.startDate}T${detail.startTime}:00`));
    }

    if (detail.endTime) {
      endDateISO = formatISO(
        parseISO(
          `${eventDate.endDate ? eventDate.endDate : eventDate.startDate}T${detail.endTime}:00`,
        ),
      );
    }

    const jsonLd: EventJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: detail.title,
      ...(startDateISO && { startDate: startDateISO }),
      ...(endDateISO && { endDate: endDateISO }),
      location: {
        '@type': 'Place',
        name: detail.location.name,
        ...(detail.location.address && {
          address: {
            '@type': 'PostalAddress',
            streetAddress: detail.location.address,
          },
        }),
        ...(detail.location.geopoint && {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: detail.location.geopoint.lat,
            longitude: detail.location.geopoint.lng,
          },
        }),
      },
      image: imageUrl ? [imageUrl] : [],
      description: detail.shortDescription,
      offers: {
        '@type': 'Offer',
        ...(detail.ticketLink && { url: detail.ticketLink }),
        priceCurrency: 'EUR',
        ...(startDateISO && { validFrom: startDateISO }),
      },
    };

    return jsonLd;
  });
}

// Convert content type to corresponding URL path
export function contentTypeToPath(contentType: string): string {
  const contentTypeMap = new Map<string, string>([
    [CONTENT_TYPE_PAGE, ''],
    [CONTENT_TYPE_EVENT, URL_PATH_EVENT],
    [CONTENT_TYPE_ARTICLE, URL_PATH_ARTICLE],
    [CONTENT_TYPE_CATEGORY, URL_PATH_CATEGORY],
  ]);

  return contentTypeMap.get(contentType) || '';
}

// Generate a Google Maps link for the event location
export const generateGoogleMapsLink = (location?: LocationData) => {
  if (!location) {
    return undefined;
  }

  let geoString = '';
  let addressString = '';

  const lat = location?.geopoint?.lat;
  const lng = location?.geopoint?.lng;
  const address = location?.address;

  if (lat && lng) {
    geoString = `&ll${lat},${lng}`;
  }

  if (address) {
    addressString = `,${location.address}`;
  }

  return `https://maps.google.com/?q=${location.name}${addressString}${geoString}`;
};
